import { Injectable, inject } from '@angular/core';

import { mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { User, UsersService } from '@rocksoft/security-sdk';
import { AuthorizationService, Permissions } from '@rocksoft/shared/authorization';
import { safeMap } from '../effects.helpers';
import * as memberUserActions from '../member-user/member-user.actions';
import { UserApiActions, UserListItemUIActions, UserListUIActions, UserResolverActions } from './user.actions';

@Injectable()
export class UserEffects {
  private readonly userService = inject(UsersService);
  private readonly actions$ = inject(Actions);
  private readonly authorizationService = inject(AuthorizationService);

  userLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserResolverActions.usersRequested),
      mergeMap(() =>
        safeMap(
          () => {
            if (!this.authorizationService.getIsAuthorized(Permissions.SecurityListUsers)) {
              throw Error('Tried to load users while unauthorized.');
            }
            return this.userService.getUsers();
          },
          (users: User[]) => UserApiActions.usersLoaded({ users }),
          (error) => UserApiActions.usersLoadedError({ error })
        )
      )
    );
  });

  userAddedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserListUIActions.userAddRequested),
      mergeMap(({ user }) =>
        safeMap(
          this.userService.addUser({ createUserCommand: user }),
          (user: User) => UserApiActions.userAdded({ user }),
          (error) => UserApiActions.userAddedError({ error })
        )
      )
    );
  });

  userDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserListItemUIActions.userDeleteRequested),
      mergeMap(({ user }) =>
        safeMap(
          this.userService.removeUser({ id: user.id }),
          () => [UserApiActions.userDeleted({ user }), memberUserActions.UserApiActions.userDeleted({ user })],
          (error) => UserApiActions.userDeletedError({ error })
        ).pipe(mergeMap((result) => (Array.isArray(result) ? result : [result])))
      )
    );
  });
}
